import React from 'react';
import _ from 'lodash';
import {Segment, Grid, Header, Divider} from 'semantic-ui-react';

import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {safeAdd, safeMult, convertValueToDollar} from 'common/utils';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {BudgetUtils} from 'app-ui-componentlibrary/dist/go/components/common/BudgetUtils';
import {Alert} from 'componentLibrary';
import {BOCS} from 'common/requestDetails/constants.js';

const DIRECT_CHARGES_KEY = 'totalDirectCharges';
const INDIRECT_CHARGES_KEY = 'totalIndirectCharges';
const TOTAL_CHARGES_KEY = 'totalCharges';
const APPSHARE_BREAKDOWN_PATH = 'share.appShare';
const REMARKS_PATH = 'nonFederal.remarks';
const PROGRAM_INCOME_PATH = 'nonFederal.constructionProgramIncome';
const INDIRECT_CHARGES_BOCS = ['indirectCharges'];
const NON_FEDERAL_PATH = 'context.awardRecommendation.payload.budget.nonFederal';
const ENTITY_TYPE = 'awardRecommendation';

const TOTAL_MANAGEMENT_COST = 'totalManagementCosts';

const BOC_CONFIGS = {
  personnel: {label: 'Personnel', order: 1},
  fringeBenefits: {label: 'Fringe benefits', order: 2},
  travel: {label: 'Travel', order: 3},
  equipment: {label: 'Equipment', order: 4},
  supplies: {label: 'Supplies', order: 5},
  contractual: {label: 'Contractual', order: 6},
  construction: {label: 'Construction', order: 7},
  other: {label: 'Other', order: 8},
};

export class ConstructionBudgetSummaryTable extends React.Component {
  constructor(props) {
    super(props);

    const modelTemplate = {
      applicant: 0,
      state: 0,
      other: 0,
      remarks: '',
      programIncome: 0,
      constructionProgramIncome: 0,
    };

    const appShareUpdated = _.get(props, 'budget.share.appShareUpdated');
    const nonFederalSaved = _.get(props, 'budget.nonFederal');
    const mergedModel = _.merge(modelTemplate, nonFederalSaved);
    this.state = {
      model: mergedModel,
      appShareUpdated: appShareUpdated,
    };
    if (!_.isEqual(mergedModel, nonFederalSaved) && !_.get(props, 'config.readOnly')) {
      this.saveNonFederal(mergedModel);
    }
  }

  componentDidUpdate = (prevProps) => {
    let appShareUpdated = _.get(this.props, 'budget.share.appShareUpdated');
    if (this.state.appShareUpdated !== appShareUpdated) {
      this.setState({
        appShareUpdated: appShareUpdated,
      });
    }
  };

  renderProgramIncome = (
    income = 0,
    numPeriods,
    readOnly = true,
    hidePeriodBreakdown,
    hideProgramIncomeWarning
  ) => {
    // IF hidePeriodBreakdown THEN Row -> 3 cells: Header, Empty cell, Cell with total value
    // IF NOT hidePeriodBreakdown THEN Row -> Header, numPeriod number of cells, Cell with total
    return (
      <React.Fragment>
        <FemaGoTable.Row background="tableDividerBorder-top">
          <FemaGoTable.TextCell>Project (program) income</FemaGoTable.TextCell>
          <FemaGoTable.TextCell colSpan={hidePeriodBreakdown ? 1 : numPeriods}>
            {_.parseInt(this.state.model.constructionProgramIncome) > 0 &&
              !hideProgramIncomeWarning && (
                <Alert
                  header="Program income"
                  content="Although not common, recipients may generate income in the course of carrying out grant-supported activities during the period of performance under the FEMA grant award. This is referred to as program income. This income can be used to defray program costs, where appropriate, consistent with 2 CFR § 200.307. This response should be $0 unless the recipient anticipates generating program income during the period of performance."
                  type="warning"
                  dismissable={false}
                />
              )}
          </FemaGoTable.TextCell>

          {readOnly ? (
            <FemaGoTable.NumberCell>
              {convertValueToDollar(income)}
            </FemaGoTable.NumberCell>
          ) : (
            <FemaGoTable.DollarCell
              width={4}
              value={_.get(this.state.model, 'constructionProgramIncome', 0)}
              title="Required field: number"
              id={'budgetConstructionProgramIncome'}
              name={'constructionProgramIncome'}
              displayError={
                this.getSpecificError('constructionProgramIncome').displayError
              }
              handleBlur={() => {
                this.saveNonFederal(this.state.model);
              }}
              handleChange={(name, value) => {
                let updatedModel = _.cloneDeep(this.state.model);
                _.set(updatedModel, name, value);
                this.setState({model: updatedModel});
              }}
            />
          )}
        </FemaGoTable.Row>
      </React.Fragment>
    );
  };

  renderItemCodeHeaders = () => (
    <FemaGoTable.Row>
      <FemaGoTable.HeaderCell>Cost classification</FemaGoTable.HeaderCell>
      <FemaGoTable.HeaderCell type="number"></FemaGoTable.HeaderCell>
      <FemaGoTable.HeaderCell type="number">Total cost</FemaGoTable.HeaderCell>
    </FemaGoTable.Row>
  );

  getItemCodeDisplayOrder = (itemConfig) => {
    let returnValue = [];
    _.forOwn(itemConfig, (value, key) => {
      returnValue[value.order - 1] = key;
    });

    return returnValue;
  };

  renderItems = (item, itemConfig, budgetSummary) => (
    <FemaGoTable.Row>
      <FemaGoTable.TextCell width={6}>
        {_.get(itemConfig, item, {}).label}
      </FemaGoTable.TextCell>
      <FemaGoTable.NumberCell></FemaGoTable.NumberCell>
      <FemaGoTable.NumberCell>
        {convertValueToDollar(
          budgetSummary && budgetSummary[item] ? budgetSummary[item] : 0
        )}
      </FemaGoTable.NumberCell>
    </FemaGoTable.Row>
  );

  renderItemsOfCost = (itemConfig, itemCodeDisplayOrder, budgetSummary) => (
    <React.Fragment>
      {_.map(itemCodeDisplayOrder, (item) =>
        this.renderItems(item, itemConfig, budgetSummary)
      )}
    </React.Fragment>
  );

  renderItemsSubtotal = (budgetSummary) => {
    return (
      <React.Fragment>
        <FemaGoTable.Row>
          <FemaGoTable.TextCell>
            <b>SUBTOTAL</b>
          </FemaGoTable.TextCell>
          <FemaGoTable.NumberCell></FemaGoTable.NumberCell>
          <FemaGoTable.NumberCell>
            {convertValueToDollar(
              budgetSummary && budgetSummary.itemsSubtotal
                ? budgetSummary.itemsSubtotal
                : 0
            )}
          </FemaGoTable.NumberCell>
        </FemaGoTable.Row>
      </React.Fragment>
    );
  };

  renderContingencies = (budgetSummary) => {
    return (
      <React.Fragment>
        <FemaGoTable.Row background="tableDividerBorder-top">
          <React.Fragment>
            <FemaGoTable.TextCell>Contingencies</FemaGoTable.TextCell>
          </React.Fragment>
          <React.Fragment>
            <FemaGoTable.NumberCell></FemaGoTable.NumberCell>
          </React.Fragment>
          <React.Fragment>
            <FemaGoTable.NumberCell>
              {convertValueToDollar(
                budgetSummary && budgetSummary.contingenciesSubTotal
                  ? budgetSummary.contingenciesSubTotal
                  : 0
              )}
            </FemaGoTable.NumberCell>
          </React.Fragment>
        </FemaGoTable.Row>
      </React.Fragment>
    );
  };

  renderContingenciesSubtotal = (budgetSummary) => {
    return (
      <React.Fragment>
        <FemaGoTable.Row>
          <React.Fragment>
            <FemaGoTable.TextCell>
              <b>SUBTOTAL</b>
            </FemaGoTable.TextCell>
          </React.Fragment>
          <React.Fragment>
            <FemaGoTable.NumberCell></FemaGoTable.NumberCell>
          </React.Fragment>
          <React.Fragment>
            <FemaGoTable.NumberCell>
              {convertValueToDollar(
                budgetSummary && budgetSummary.itemsAndContingenciesSubTotal
                  ? budgetSummary.itemsAndContingenciesSubTotal
                  : 0
              )}
            </FemaGoTable.NumberCell>
          </React.Fragment>
        </FemaGoTable.Row>
      </React.Fragment>
    );
  };

  renderProjectTotal = (budgetSummary) => {
    return (
      <React.Fragment>
        <FemaGoTable.Row background="tableDividerBorder-top tableDividerBorder-bottom">
          <React.Fragment>
            <FemaGoTable.TextCell>
              <b>TOTAL PROJECT COSTS</b>
            </FemaGoTable.TextCell>
          </React.Fragment>
          <React.Fragment>
            <FemaGoTable.NumberCell></FemaGoTable.NumberCell>
          </React.Fragment>
          <React.Fragment>
            <FemaGoTable.NumberCell>
              {convertValueToDollar(
                budgetSummary && budgetSummary.totalMinusProjectIncome
                  ? budgetSummary.totalMinusProjectIncome
                  : 0
              )}
            </FemaGoTable.NumberCell>
          </React.Fragment>
        </FemaGoTable.Row>
      </React.Fragment>
    );
  };

  getSpecificError = (field) => {
    const errParent = _.find(this.props.validationErrors, (errorObj) => {
      return _.has(errorObj, field);
    });
    const errorsForField = _.get(errParent, field);
    const firstError = _.find(errorsForField, (err) => !err.isValid) || {
      isValid: true,
    };
    return {
      displayError: !firstError.isValid,
      errorMessages: firstError.message,
    };
  };

  saveNonFederal = (value) => {
    const {config} = this.props;
    const {nonFederalPath, entityType} = config;

    if (nonFederalPath !== undefined) {
      this.props.saveEntityData(
        nonFederalPath || NON_FEDERAL_PATH,
        entityType || ENTITY_TYPE,
        value,
        'budget'
      );
    }
  };

  render() {
    let {budget, config, fundingStreams, foConfig} = this.props;
    const hideFinalSubtotalAndProgramIncome = _.get(
      this.props,
      'hideFinalSubtotalAndProgramIncome',
      false
    );

    if (budget && foConfig) {
      const {extras, share} = budget;

      const bocs = BOCS;
      const {
        hidePeriodBreakdown,
        isProgramIncomeLast = false,
        hideProgramIncomeWarning = false,
        showManagementCost = false,
        maxPercentageOfManagementCost = 10,
        readOnlyNonFed,
        readOnlyRemarks,
        readOnlyProgramIncome,
        header,
        hideHeader,
      } = config;
      const readOnly = !!config.readOnly;
      const isNonFedReadOnly = _.isUndefined(readOnlyNonFed) ? readOnly : readOnlyNonFed;
      const isRemarksReadOnly = _.isUndefined(readOnlyRemarks)
        ? readOnly
        : readOnlyRemarks;
      const isProgramIncomeReadOnly = _.isUndefined(readOnlyProgramIncome)
        ? readOnly
        : readOnlyProgramIncome;
      const directChargeBocs = _.sortBy(
        _.difference(bocs, INDIRECT_CHARGES_BOCS),
        (a) => _.get(BOC_CONFIGS, a, {}).order
      );

      let numPeriods = _.get(budget, APPSHARE_BREAKDOWN_PATH, []).length;
      let colSpan = hidePeriodBreakdown ? 3 : numPeriods + 2;

      if (_.get(this.props, 'itemConfig')) {
        const budgetSummary = _.get(budget, 'constructionBudgetSummary');
        const itemConfig = _.get(this.props, 'itemConfig');
        const itemCodeDisplayOrder = this.getItemCodeDisplayOrder(this.props.itemConfig);

        return (
          <React.Fragment>
            {!hideHeader && (
              <Header as="h4">{header ? header : 'Construction budget summary'}</Header>
            )}
            <FemaGoTable summary="Construction Budget summary table">
              <FemaGoTable.Header>{this.renderItemCodeHeaders()}</FemaGoTable.Header>
              <FemaGoTable.Body>
                {this.renderItemsOfCost(itemConfig, itemCodeDisplayOrder, budgetSummary)}
                {this.renderItemsSubtotal(budgetSummary)}
                {this.renderContingencies(budgetSummary)}
                {!hideFinalSubtotalAndProgramIncome &&
                  this.renderContingenciesSubtotal(budgetSummary)}
                {!hideFinalSubtotalAndProgramIncome &&
                  this.renderProgramIncome(
                    _.get(budget, PROGRAM_INCOME_PATH),
                    numPeriods,
                    isProgramIncomeReadOnly,
                    hidePeriodBreakdown,
                    hideProgramIncomeWarning
                  )}
                {this.renderProjectTotal(budgetSummary)}
              </FemaGoTable.Body>
            </FemaGoTable>
            {!_.isEmpty(_.get(extras, 'message')) && (
              <Alert
                size="small"
                header=""
                content={_.get(extras, 'message.text')}
                type={_.get(extras, 'message.type', 'info')}
                dismissable={false}
              />
            )}
          </React.Fragment>
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
}

export default connectDynamic(ConstructionBudgetSummaryTable);
